import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import HomeSlider from "./Home/homeSlider";
import Services from "./Home/services";
import OurBlogPost from "./Home/ourblogpost";
import {
  TwoColRow,
  ColText,
  ColImage,
  ColImg,
  WideSection,
  FiveColRow,
  IconCard,
  IconCardImage,
  IconCardLabel,
  IconCardDesc,
  TwoColContentRow,
  ColBullets,
  ColImageRight,
} from "../components/ContentLayouts";

// ── Image imports ─────────────────────────────────────────────────────────────
import imgWelcome      from "../assets/images/content/homepage/welcome.jpg";
import iconWhyChoose1  from "../assets/images/icons/location.png";
import iconWhyChoose2  from "../assets/images/icons/search.png";
import iconWhyChoose3  from "../assets/images/icons/verified.png";
import iconWhyChoose4  from "../assets/images/icons/med-calendar.png";
import iconWhyChoose5  from "../assets/images/icons/select-app.png";
import imgExplore      from "../assets/images/content/homepage/explore.jpg";
import imgBookVet      from "../assets/images/content/homepage/book-vet.jpg";

const faqs = [
  {
    question: "What veterinary services does PetQura offer?",
    answer:
      "PetQura connects you with facilities offering a wide range of services, including vaccinations, dental care, grooming, diagnostics, and surgery. You can find everything from a local pet clinic for routine care to a fully equipped veterinary hospital for complex treatments.",
  },
  {
    question: "Why should I choose PetQura for my pet's healthcare?",
    answer:
      "We make it simple to find the closest, most qualified veterinary clinic based on the specific service you need. Instead of searching \"best vet doctor near me\" or \"best pet clinic near me\", our platform reduces stress by putting all the information, reviews, and booking tools in one easy-to-use location.",
  },
  {
    question:
      "How can I find the best pet doctor near me through PetQura?",
    answer:
      "It is very straightforward! Simply enter your location and desired service on our platform, browse the available clinics, select a time, and book vet appointment online instantly.",
  },
  {
    question: "Do you provide preventive healthcare services for pets?",
    answer:
      "Yes, you can easily use our platform to find a pet clinic that specializes in preventive care, including annual wellness exams, parasite control, and nutritional counseling.",
  },
  {
    question: "What types of pets are treated?",
    answer:
      "Most clinics focus on dogs and cats. You can filter your search to find a veterinary hospital for the service that you are looking for.",
  },
  {
    question: "What should I do if I need an emergency vet appointment?",
    answer:
      "If your pet is experiencing a crisis, use our search feature to instantly locate the nearest open clinic and secure an emergency vet appointment so they can prepare for your pet's arrival.",
  },
];

const whyChooseCards = [
  {
    icon: iconWhyChoose1,
    label: "Location-Based Matching",
    desc: "Locate a veterinary hospital near you, reducing travel anxiety for your pet and saving you time.",
  },
  {
    icon: iconWhyChoose2,
    label: "Service-Specific Searches",
    desc: "Filter your search by the exact care you need, so you always find the right pet clinic for your specific situation.",
  },
  {
    icon: iconWhyChoose3,
    label: "Verified Professionals",
    desc: "Read the reviews of pet doctor online from other pet parents and select the best pet doctor for your pet.",
  },
  {
    icon: iconWhyChoose4,
    label: "At Your Convenience",
    desc: "Book vet appointment at the time and date that fits you best to avoid the trouble of waiting for long durations at veterinary clinics.",
  },
  {
    icon: iconWhyChoose5,
    label: "All-in-One Platform",
    desc: "From standard annual check-ups to specialized medical treatments, effortlessly manage your pet's entire health journey in one place.",
  },
];

const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <FaqRow>
      <FaqQuestion onClick={() => setOpen(!open)}>
        <span>{question}</span>
        <FaqIcon>{open ? "−" : "+"}</FaqIcon>
      </FaqQuestion>
      {open && <FaqAnswer>{answer}</FaqAnswer>}
    </FaqRow>
  );
};

const HomePage = () => {
  const history = useHistory();

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>
          Book Pet Clinic &amp; Vet Appointments Online | PetQura Bangalore
        </title>
        <meta
          name="description"
          content="Find verified pet clinics and veterinary hospitals in Bangalore and across India. Book a vet appointment online, consult an online vet doctor or find emergency vet care - all in one place. Pet care near you, made simple."
        />
      </Helmet>

      {/* ── Banner with H1 overlay ── */}
      <BannerWrapper>
        <HomeSlider page={"home"} />
        <H1Overlay>
          <h1>
            Find Trusted Pet Clinics &amp; Veterinary Hospitals Near You
          </h1>
        </H1Overlay>
      </BannerWrapper>

      {/* ── Welcome section — Pattern A (Welcome variant) ── */}
      <PageWrapper>
        <ContentSection>
          <TwoColRow>
            <ColText>
              <SectionHeading>
                Welcome to PetQura: Your Trusted Veterinary Hospital &amp; Pet
                Clinic Platform
              </SectionHeading>
              <ColImg
                src={imgWelcome}
                alt="Welcome to PetQura"
              />
            </ColText>
            <ColImage>
              <p>
                Being a pet parent brings immense joy, but finding the right
                healthcare when your furry friend needs it can sometimes feel
                overwhelming. At PetQura, we make this process easy and
                stress-free by helping you find a trusted veterinary hospital or
                pet clinic right in your neighbourhood. Whether you are looking
                for a routine wellness check-up, need to consult the best
                veterinary doctor near you for a quick question, or require
                immediate assistance for a sick pet, our platform connects you
                directly with the closest pet clinic based on the exact service
                you desire. This streamlined approach means less stress for you
                and a much happier, healthier experience for your pets.
              </p>
            </ColImage>
          </TwoColRow>
        </ContentSection>
      </PageWrapper>

      {/* ── Services Slider ── */}
      <Services />

      {/* ── Why Choose section — Pattern B (5-col icon cards) ── */}
      <WideSection>
        <ContentSection>
          <SectionHeading>
            Why Choose Our Veterinary Clinic Services?
          </SectionHeading>
          <p>
            Finding a reliable veterinary clinic should not be a difficult
            guessing game. We built PetQura to give you complete peace of mind
            and ultimate convenience when managing your pet's health. If you
            wonder "who is the best veterinary doctor near me" or "where is the
            closest veterinary hospital near me", we help you find verified,
            qualified vets near you who truly care about animal well-being. By
            keeping everything organized in one place, we take the hassle out of
            pet healthcare so you can spend more quality time with your
            companions. Through PetQura, you get:
          </p>
          <FiveColRow>
            {whyChooseCards.map((card, idx) => (
              <IconCard key={idx}>
                <IconCardImage src={card.icon} alt={card.label} />
                <IconCardLabel>{card.label}</IconCardLabel>
                <IconCardDesc>{card.desc}</IconCardDesc>
              </IconCard>
            ))}
          </FiveColRow>
        </ContentSection>
      </WideSection>

      {/* ── Explore section — Pattern D (2-row: intro / bullets+image) ── */}
      <PageWrapper>
        <ContentSection>
          <SectionHeading>
            Explore Our Full Range of Pet Care Services at PetQura
          </SectionHeading>
          <p>
            By using PetQura, you can easily discover pet clinics near you that
            offer a comprehensive suite of medical and grooming services near
            you. We know that getting a sick or anxious pet into the car isn't
            always easy. We can help you with:
          </p>
          <TwoColContentRow>
            <ColBullets>
              <BulletList>
                <li>
                  <strong>Vaccinations &amp; Wellness:</strong> Keep your pets
                  protected with timely immunizations and general check-ups at a
                  nearby veterinary clinic.
                </li>
                <li>
                  <strong>Grooming &amp; Hygiene:</strong> Find specialized
                  clinics that offer complete hygiene, bathing, and grooming
                  services to keep your pet comfortable and clean.
                </li>
                <li>
                  <strong>Dental Care:</strong> Easily locate a veterinary
                  hospital fully equipped for professional dental cleanings,
                  extractions, and oral health assessments.
                </li>
                <li>
                  <strong>Advanced Diagnostics:</strong> Access modern
                  facilities equipped with x-rays, comprehensive bloodwork, and
                  ultrasound capabilities for accurate and fast assessments.
                </li>
                <li>
                  <strong>Emergency vet appointment:</strong> Find a pet doctor
                  online to book quick appointments at a veterinary hospital
                  near you, giving timely care to your pet.
                </li>
              </BulletList>
            </ColBullets>
            <ColImageRight>
              <ColImg
                src={imgExplore}
                alt="Explore pet care services at PetQura"
              />
            </ColImageRight>
          </TwoColContentRow>
        </ContentSection>
      </PageWrapper>

      {/* ── Our Blog Post ── */}
      <OurBlogPost />

      {/* ── Book a Vet section — Pattern D (2-row: intro / bullets+image) ── */}
      <PageWrapper>
        <ContentSection>
          <SectionHeading>Book a Vet Appointment in Minutes</SectionHeading>
          <p>
            We know your time is valuable, and dealing with a sick pet is
            stressful enough without administrative hurdles. That is exactly why
            we made it incredibly simple to book vet appointment slots through
            PetQura. Similar to meeting an online veterinary doctor, you don't
            have to wait for uncertain durations or struggle to find a slot that
            fits your busy schedule. You get these benefits and the assurance
            that comes with physical consultations. With our platform,
            coordinating a visit to a veterinary hospital is seamless, giving
            you more time to focus on what really matters — caring for your pet.
          </p>
          <TwoColContentRow>
            <ColBullets>
              <BulletList>
                <li>
                  <strong>Simple Scheduling:</strong> Browse available dates
                  and times at your chosen pet clinic near you and select the
                  slot that works best for your family's routine.
                </li>
                <li>
                  <strong>Online Convenience:</strong> When you book vet
                  appointment online through our platform, you receive instant
                  confirmation and peace of mind.
                </li>
                <li>
                  <strong>Packed Schedule:</strong> Book vet appointment with
                  the best veterinary doctor in India and avoid the hassle of
                  waiting for endless duration at the veterinary clinic.
                </li>
                <li>
                  <strong>Helpful Reminders:</strong> We help you keep track of
                  your upcoming visits with friendly notifications so you never
                  miss an important check-up at the veterinary clinic.
                </li>
              </BulletList>
            </ColBullets>
            <ColImageRight>
              <ColImg
                src={imgBookVet}
                alt="Book a vet appointment in minutes"
              />
            </ColImageRight>
          </TwoColContentRow>
        </ContentSection>

        <ContentSection>
          <SectionHeading>
            Need Help Fast? Emergency Vet Appointments
          </SectionHeading>
          <p>
            Accidents and sudden illnesses are incredibly stressful, but getting
            immediate help shouldn't be. When your pet needs urgent, unexpected
            care, our platform helps you secure an emergency vet appointment
            without any unnecessary delays. We instantly show you the closest
            veterinary clinic near you that is currently open and fully equipped
            to handle urgent medical cases, so you can focus entirely on getting
            your pet the help they need right away.
          </p>
          <BulletList>
            <li>
              <strong>Immediate Routing:</strong> Quickly find the nearest open
              veterinary hospital that is ready for emergency arrivals.
            </li>
            <li>
              <strong>Priority Booking:</strong> Book vet appointment with a
              few taps so the medical staff knows you are on the way and can
              prepare for your arrival.
            </li>
          </BulletList>
        </ContentSection>

        <ContentSection>
          <SectionHeading>Frequently Asked Questions</SectionHeading>
          <FaqList>
            {faqs.map((faq, idx) => (
              <FaqItem key={idx} question={faq.question} answer={faq.answer} />
            ))}
          </FaqList>
        </ContentSection>
      </PageWrapper>
    </>
  );
};

export default HomePage;

// ─── Styled Components ────────────────────────────────────────────────────────

const BannerWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const H1Overlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.65) 0%, transparent 100%);
  padding: 2.5rem 2rem 1.75rem;

  h1 {
    max-width: 1400px;
    margin: 0 auto;
    font-size: 2rem;
    font-weight: 700;
    color: #ffffff;
    font-family: Montserrat, sans-serif;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
    line-height: 1.3;

    @media (max-width: 768px) {
      font-size: 1.35rem;
    }
  }
`;

const PageWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 5%;

  @media (max-width: 768px) {
    padding: 2rem 5%;
  }
`;

const ContentSection = styled.section`
  margin-bottom: 2.5rem;

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 1rem;
  }
`;

const SectionHeading = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 1rem;
  font-family: Montserrat, sans-serif;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const BulletList = styled.ul`
  padding-left: 1.4rem;
  margin-bottom: 1rem;

  li {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 0.6rem;
  }
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
`;

const FaqRow = styled.div`
  border-bottom: 1px solid #e5e5e5;

  &:last-child {
    border-bottom: none;
  }
`;

const FaqQuestion = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border: none;
  padding: 1rem 1.25rem;
  text-align: left;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  font-family: Montserrat, sans-serif;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f7f9ff;
  }
`;

const FaqIcon = styled.span`
  font-size: 20px;
  font-weight: 400;
  color: #00419d;
  flex-shrink: 0;
  margin-left: 1rem;
`;

const FaqAnswer = styled.div`
  padding: 0 1.25rem 1rem;
  font-size: 15px;
  line-height: 1.75;
  color: #555555;
  font-family: Montserrat, sans-serif;
  background: #fafafa;
`;
